import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from 'react-bootstrap/Alert';
import Container from 'react-bootstrap/Container';
import { t } from '../../../i18n';
import { loadCoursesSpecificCourseInformation } from './redux/actions';
import { getSpecificCourseState } from '../../selectors';
import UrlTo from '../../../url-to';
import BreadCrumbs from '../../components/breadcrumbs/index';
import CourseVideoModal from '../../components/course-video-modal';
import Reviews from './components/reviews';
import RelatedCourses from './components/related-courses';
import PriceAndSummaryBlock from './components/price';
import Instructor from './components/instructor';
import EpisodesList from './components/episodes-list';
import Description from './components/description';
import Goals from './components/goals';
import Preview from './components/preview';
import LineLoader from '../../components/line-loader';
import { ButtonLink, Loader } from '../../../components';
import { shoppingCartIcon } from '../../assets';
import { formatCoursePrice } from '../../utilities';
import { logError } from '../../../utilities/log';
import getCampaignPromocode from '../../getCampaignPromocode';
import './styles.scss';

const SpecificCourseScreen = ({ match }) => {
  const [currentVideo, setCurrentVideo] = useState(null);
  const courseId = match.params?.id;
  const dispatch = useDispatch();
  const { data, isLoading, hasError } = useSelector(getSpecificCourseState);

  useEffect(() => {
    const fetchCourse = async () => {
      const promocode = getCampaignPromocode();
      try {
        await dispatch(loadCoursesSpecificCourseInformation(courseId, { promocode }));
      } catch (e) {
        logError('Courses/SpecificCourse', 'Unhandled error when loading course', {
          courseId,
          promocode,
          message: e.message,
        });
      }
    };
    fetchCourse();
  }, [courseId]);

  const handlePlayVideo = video => {
    setCurrentVideo(video);
  };

  if (hasError) {
    return <Alert variant="danger">{t('SERVER_ERROR')}</Alert>;
  }

  if (isLoading) {
    return (
      <div className="specific-course-screen specific-course-screen__loading-indication-wrapper">
        <LineLoader hide={!isLoading} className="specific-course-screen__line-loader" />
        <Loader />
      </div>
    );
  }

  return (
    <Container fluid className="specific-course-screen">
      <Container>
        <BreadCrumbs
          navigationLinks={[
            { url: UrlTo.coursesHome(), name: t('DIGITAL_COURSES.HOME_SCREEN.NAME'), isActive: false },
            { name: t('DIGITAL_COURSES.SPECIFIC_COURSE_SCREEN.NAME'), isActive: true },
          ]}
        />
        <div className="specific-course-screen__layout">
          <Preview
            className="specific-course-screen__layout-preview"
            title={data.title}
            previewImageUrlSmall={data.previewImageUrlSmall}
            previewImageUrlLarge={data.previewImageUrlLarge}
            previewVideo={data.previewVideo}
            onPlayCoursePreview={handlePlayVideo}
          />

          <PriceAndSummaryBlock
            className="specific-course-screen__layout-price"
            courseId={data.id}
            price={data.price}
            hoursTotal={data.hoursTotal}
            lessonsTotal={data.lessonsTotal}
            downloadableMaterialsAreAvailable={data.downloadableMaterialsAreAvailable}
            courseIsPurchased={data.isPurchased}
            courseIsStarted={data.isStarted}
          />

          <Instructor author={data.author} className="specific-course-screen__layout-instructor" />

          <Description description={data.description} className="specific-course-screen__layout-description" />

          <Goals goals={data.goalsDataSource} className="specific-course-screen__layout-goals" />

          <EpisodesList
            courseId={data.id}
            isCoursePurchased={data.isPurchased}
            episodes={data.lessonsDataSource}
            onPlayEpisode={handlePlayVideo}
            className="specific-course-screen__layout-episodes"
          />
        </div>

        {data.price.currency && !data.isPurchased && (
          <ButtonLink
            href={UrlTo.coursesCheckout(data.id)}
            type="green"
            className="specific-course-screen__buy-course-button"
            linkClassName="specific-course-screen__buy-course-button-wrapper"
            text={
              <span>
                <img src={shoppingCartIcon} alt="shopping-cart" />
                {`${t('DIGITAL_COURSES.SPECIFIC_COURSE_SCREEN.BUY_COURSE')} - ${formatCoursePrice(data.price)}`}
              </span>
            }
          />
        )}
      </Container>
      {data.reviewsDataSource.length > 0 && <Reviews reviews={data.reviewsDataSource} isLoading={isLoading} />}
      {data.relatedCoursesDataSource.length > 0 && (
        <RelatedCourses
          relatedCourses={data.relatedCoursesDataSource}
          handlePlayVideo={handlePlayVideo}
          isLoading={isLoading}
        />
      )}
      <CourseVideoModal visible={!!currentVideo} video={currentVideo} onClose={() => setCurrentVideo(null)} />
    </Container>
  );
};

export default SpecificCourseScreen;
